import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { applyFilters } from '../helpers/FilterApplicator';
import useClassifications from './useClassifications';
import { buildTableColumns } from './classificationTableConfig';
import CreateClassificationForm from './CreateClassificationForm';
import { AltTakeover } from '../../takeover';
import ClassificationsTable from './ClassificationsTable';
import EditTakeover from '../edit-takeover';
import ClassificationEmptyState from './ClassificationEmptyState';

const getClassificationFilters = classification => ({
        chains: classification.chains,
        turnovers: classification.turnovers,
        salesSpaceGroups: classification.salesSpaceGroups,
        segments: classification.segments,
        unitsIncludeExclude: classification.unitsIncludeExclude
});

const getClassificationUnits = (classification, baseUnits, type, allUnits) =>
    applyFilters(baseUnits, getClassificationFilters(classification), type, allUnits);

const calculateClassificationMetrics = (classification, baseUnits, type, allUnits) => {
    const selectedFrequency =
        classification.classificationFrequencies?.find(freq => freq.selected) ||
        classification.classificationFrequencies?.[0];
    const frequencyValue = Number(selectedFrequency?.classificationFrequencyValue) || 0;

    if (!frequencyValue) {
        return { visitingTime: 0, frequency: 0 };
    }

    const selectedVisitingTime =
        classification.classificationVisitingTimes?.find(time => time.selected) ||
        classification.classificationVisitingTimes?.[0];
    const visitingTimeMinutes = Number(selectedVisitingTime?.classificationVistingTimeValue) || 0;

    const unitsForClassification = getClassificationUnits(classification, baseUnits, type, allUnits);
    const unitCount = unitsForClassification.length;

    return {
        visitingTime: visitingTimeMinutes ? visitingTimeMinutes * unitCount * frequencyValue : 0,
        frequency: unitCount * frequencyValue
    };
};

const InlineEditClassificationButton = ({
    classificationId,
    scenarioId,
    className,
    label,
    closeTakeover,
    onSaved,
    formProps
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const toggleOpen = () => {
        setIsOpen(!isOpen);
    };

    return (
        <>
            <button type="button" className={className} onClick={toggleOpen}>
                {label}
            </button>
            <AltTakeover isOpen={isOpen} toggleTakeover={toggleOpen}>
                {isOpen && (
                    <CreateClassificationForm
                        {...formProps}
                        isEdit={true}
                        classificationId={classificationId}
                        scenarioId={scenarioId}
                        saveSuccessCallback={() => {
                            toggleOpen();
                            if (onSaved) {
                                onSaved();
                            }
                        }}
                        cancelButton={
                            <button onClick={toggleOpen} className="classification-form__cancel">
                                Avbryt
                            </button>
                        }
                    />
                )}
            </AltTakeover>
        </>
    );
};

const Classification = ({
    allUnits = [],
    units: districtUnits = [],
    selectedUnits = [],
    ...props
}) => {
    const [isCreateClassificationOpen, setIsCreateClassificationOpen] = useState(false);
    const [isManageClassificationsOpen, setIsManageClassificationsOpen] = useState(false);
    const { scenarioId: routeScenarioId } = useParams();
    const scenarioId = routeScenarioId || null;
    const { data: classifications = [], isLoading, reload } = useClassifications(scenarioId, props.fetch);
    const { salesDistrictFilters, type } = props;

    const targetUnitIds = useMemo(() => {
        if (selectedUnits?.length) return selectedUnits;
        if (districtUnits?.length) return districtUnits;
        return [];
    }, [selectedUnits, districtUnits]);

    const unitsInDistrict = useMemo(() => {
        if (!targetUnitIds.length) {
            return [];
        }
        const idSet = new Set(targetUnitIds);
        return allUnits.filter(unit => idSet.has(unit.companyId));
    }, [allUnits, targetUnitIds]);

    const filteredUnits = applyFilters(
        unitsInDistrict,
        salesDistrictFilters,
        type,
        allUnits
    );

    const { totalVisitingTime, totalFrequency } = useMemo(() => {
        if (!classifications.length) return { totalVisitingTime: 0, totalFrequency: 0 };
        return classifications.reduce(
            (totals, classification) => {
                const metrics = calculateClassificationMetrics(
                    classification,
                    filteredUnits,
                    type,
                    allUnits
                );
                return {
                    totalVisitingTime: totals.totalVisitingTime + metrics.visitingTime,
                    totalFrequency: totals.totalFrequency + metrics.frequency
                };
            },
            { totalVisitingTime: 0, totalFrequency: 0 }
        );
    }, [classifications, filteredUnits, type, allUnits]);

    const columns = useMemo(() => buildTableColumns({
        renderActions: (row) => (
            <EditTakeover
                id={row.classificationId}
                isActive={true}
                createUrl={`/scenario/${row.salesDistrictScenarioId}/classifications/create`}
                listUrl={`/scenario`}
                isDistrictEdit={false}
                editButton="Ändra klassificering"
                confirmText="Vill du ta bort klassificering permanent?"
                deleteHeading="TA BORT KLASSIFICERING"
                deleteButtonText="Ta bort klassificering"
                scenarioId={row.salesDistrictScenarioId}
                onUpdate={reload}
                onCloseAfterUpdate={() => setIsManageClassificationsOpen(false)}
                renderEditButton={({ closeTakeover, className, label }) => (
                    <InlineEditClassificationButton
                        classificationId={row.classificationId}
                        scenarioId={row.salesDistrictScenarioId}
                        className={className}
                        label={label}
                        closeTakeover={closeTakeover}
                        formProps={props}
                        onSaved={() => {
                            reload();
                            closeTakeover();
                        }}
                    />
                )}
            />
        )
    }), [reload, props]);

    const openCreateClassificationForm = () => setIsCreateClassificationOpen(true);
    const closeCreateClassificationForm = () => setIsCreateClassificationOpen(false);

    return (
        <div className="create-district-map__footer-col">
            <h2 className="create-district-map__footer-heading">Frekvens / Besökstid</h2>
            <div className="create-district-map__footer-value-container">
                <span className="create-district-map__footer-value">
                    {!isLoading && classifications.length > 0 && `${Math.floor(totalFrequency)} besök / ${totalVisitingTime.toString()} h`}
                </span>
                {!isLoading && (
                    <div className="classification-actions">
                        {classifications.length === 0 && (
                            <button type="button" onClick={openCreateClassificationForm} className="create-district-map__footer-value-create-button"><span>+ Ny klassificering</span></button>
                        )}
                        {classifications.length > 0 && (
                            <button type="button" onClick={() => setIsManageClassificationsOpen(true)} className="create-district-map__footer-value-edit-button"><span>Hantera klassificeringar</span></button>
                        )}
                    </div>
                )}
            </div>
            

            <AltTakeover 
                heading="Hantera klassificeringar"
                isOpen={isManageClassificationsOpen} 
                toggleTakeover={setIsManageClassificationsOpen}>
                <ClassificationsTable
                    rows={classifications}
                    columns={columns}
                    scrollable
                    emptyState={<ClassificationEmptyState
                        linkComponent={<button className="form__button districts__add" type="button" onClick={openCreateClassificationForm}>+ Ny klassificering</button>}
                    />}
                    afterTable={
                        <button className="form__button districts__add" type="button" onClick={openCreateClassificationForm}>+ Ny klassificering</button>
                    }
                />
            </AltTakeover>

            <AltTakeover 
                heading="Skapa ny klassificering"
                isOpen={isCreateClassificationOpen} 
                toggleTakeover={setIsCreateClassificationOpen}>
                {isCreateClassificationOpen &&
                    <CreateClassificationForm
                        {...props}
                        isEdit={false}
                        scenarioId={scenarioId}
                        saveSuccessCallback={() => {
                            closeCreateClassificationForm();
                            reload();
                        }}
                        cancelButton={<button onClick={closeCreateClassificationForm} className="classification-form__cancel">Avbryt</button>}
                    />
                }
            </AltTakeover>
        </div>
    );
};

export default Classification;